import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  QueryDocumentSnapshot,
  and,
  collection,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  or,
  orderBy,
  query,
  startAfter,
  startAt,
  where,
} from 'firebase/firestore';

import { ISanitizedCompany } from 'types/company';
import { IStory } from 'types/story';

import { clientStoryConverter } from 'services/api/firestore/converters';
import { sortLowerCase } from 'services/array';

import { db } from 'clients/firebase';

import { useUser } from 'hooks/auth/user';

const ITEMS_PER_PAGE = 8;

export const useFetchStories = (
  company: ISanitizedCompany,
  options: null | {
    orderBy: [string, 'asc' | 'desc'];
    userId?: string;
    topicFilters: string[] | null;
    isPublished?: boolean;
    isInFeed?: boolean;
    initialItems: number;
    itemsPerPage: number;
    collectionId?: string;
    inTeam?: boolean;
  },
  seed?: {
    stories: IStory[];
    total: number;
  },
) => {
  const { user } = useUser();

  const queryConstraints = useMemo(() => {
    if (!options || !options.topicFilters) {
      return null;
    }

    const queryConstraints = [where('meta.companyId', '==', company.id)];

    if (options.isPublished) {
      queryConstraints.push(where('meta.publishedAt', '!=', null));
    }

    if (!options.collectionId) {
      // TODO: decide, if stories in collections should only be visible in collections
      // for now YES, because it is hard to filter out the stories,
      // for which the user does not have the collection team here!
      queryConstraints.push(where('meta.collectionId', '==', ''));
      if (options.isInFeed) {
        queryConstraints.push(where('meta.showInFeed', '==', true));
      }
    }

    if (options.topicFilters && options.topicFilters.length > 0) {
      const topicsString = sortLowerCase(options.topicFilters).join(',');

      queryConstraints.push(
        where('meta.topicsPermutations', 'array-contains', topicsString),
      );
    }

    if (options.userId) {
      queryConstraints.push(where('meta.userId', '==', options.userId));
    }

    if (options.collectionId !== undefined) {
      queryConstraints.push(
        where('meta.collectionId', '==', options.collectionId),
      );
    }

    if (options.inTeam && (!user || !user.team || user.team.length === 0)) {
      queryConstraints.push(where('meta.team', '==', []));
    }

    return queryConstraints;
  }, [company.id, options, user]);

  const compositeFilter = useMemo(() => {
    if (!options) {
      return null;
    }

    let compositeFilter;
    if (options.inTeam && user && user.team && user.team.length > 0) {
      compositeFilter = or(
        where('meta.team', 'array-contains-any', user.team),
        where('meta.team', '==', []),
      );
    }

    return compositeFilter;
  }, [options, user]);

  const isFirst = useRef(true);
  const skipInitialFetch = useRef(!!seed);

  const [storyDocsState, setStoryDocsState] = useState<{
    storyDocs: QueryDocumentSnapshot<IStory>[] | null;
    total: number | null;
  }>({ storyDocs: null, total: seed?.total ?? null });

  const [storiesState, setStoriesState] = useState<{
    stories: IStory[] | null;
    canFetchMore: boolean;
  }>({
    stories: seed?.stories ?? null,
    canFetchMore: seed ? seed.stories.length < seed.total : false,
  });

  useEffect(() => {
    if (!seed) {
      skipInitialFetch.current = false;
      return;
    }

    skipInitialFetch.current = true;
    setStoriesState({
      stories: seed.stories,
      canFetchMore: seed.stories.length < seed.total,
    });

    if (!seed.stories.length) {
      setStoryDocsState({ storyDocs: null, total: seed.total });
      return;
    }

    (async () => {
      const storyDocs = (
        await Promise.all(
          seed.stories.map(story =>
            getDoc(
              doc(db, 'Stories', story.id).withConverter(clientStoryConverter),
            ),
          ),
        )
      ).filter(docSnapshot => docSnapshot.exists()) as QueryDocumentSnapshot<IStory>[];

      setStoryDocsState({
        total: seed.total,
        storyDocs,
      });
    })();
  }, [seed]);

  useEffect(() => {
    (async () => {
      if (!options || !queryConstraints) {
        return;
      }

      if (skipInitialFetch.current) {
        skipInitialFetch.current = false;
        return;
      }

      let baseQuery;
      if (compositeFilter) {
        baseQuery = query(
          collection(db, 'Stories'),
          and(...queryConstraints, compositeFilter),
          orderBy(...options.orderBy),
        );
      } else {
        baseQuery = query(
          collection(db, 'Stories'),
          ...queryConstraints,
          orderBy(...options.orderBy),
        );
      }

      const total = (await getCountFromServer(baseQuery)).data().count;

      const { docs: storyDocs } = await getDocs(
        query(
          baseQuery,
          limit(
            isFirst ? options.initialItems || ITEMS_PER_PAGE : ITEMS_PER_PAGE,
          ),
        ).withConverter(clientStoryConverter),
      );

      setStoryDocsState({
        total,
        storyDocs,
      });
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryConstraints]);

  useEffect(() => {
    if (storyDocsState.storyDocs && storyDocsState.total !== null) {
      setStoriesState({
        stories: storyDocsState.storyDocs.map(doc => doc.data()),
        canFetchMore: storyDocsState.storyDocs.length < storyDocsState.total,
      });
    }
  }, [storyDocsState]);

  const fetchMore = useCallback(async () => {
    if (options && storyDocsState.storyDocs && queryConstraints) {
      const [lastStoryDoc] = storyDocsState.storyDocs.slice(-1);

      const newStoryDocs = compositeFilter
        ? (
            await getDocs(
              query(
                collection(db, 'Stories'),
                and(...queryConstraints, compositeFilter),
                orderBy(...options.orderBy),
                limit(options.itemsPerPage),
                lastStoryDoc ? startAfter(lastStoryDoc) : startAt(null),
              ).withConverter(clientStoryConverter),
            )
          ).docs
        : (
            await getDocs(
              query(
                collection(db, 'Stories'),
                ...queryConstraints,
                orderBy(...options.orderBy),
                limit(options.itemsPerPage),
                lastStoryDoc ? startAfter(lastStoryDoc) : startAt(null),
              ).withConverter(clientStoryConverter),
            )
          ).docs;

      setStoryDocsState(oldState => ({
        ...oldState,
        storyDocs: [...(oldState.storyDocs || []), ...newStoryDocs],
      }));
    }
  }, [options, queryConstraints, compositeFilter, storyDocsState]);

  return {
    stories: storiesState.stories,
    canFetchMore: storiesState.canFetchMore,
    fetchMore,
  };
};
